import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Copy, Trash2, Eye, EyeOff } from "lucide-react";

export default function ApiKeys() {
  const { api } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [visibleKeys, setVisibleKeys] = useState({});
  const [page, setPage] = useState(1);
  const pageSize = 6;

  const fetchKeys = useCallback(() => {
    api("get", "/admin/api-keys")
      .then((res) => setKeys(res.data.keys || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => {
    fetchKeys();
  }, [fetchKeys]);

  const totalPages = Math.max(1, Math.ceil(keys.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const visibleKeysList = keys.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    try {
      const res = await api("post", "/admin/api-keys", { name: name.trim() });
      setKeys([res.data, ...keys]);
      setPage(1);
      setShowCreate(false);
      setName("");
      toast.success("API Key berhasil dibuat!");
      setVisibleKeys((v) => ({ ...v, [res.data.id]: true }));
    } catch {
      toast.error("Gagal membuat API Key");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Yakin hapus API Key ini?")) return;
    try {
      await api("delete", `/admin/api-keys/${id}`);
      setKeys(keys.filter((k) => k.id !== id));
      toast.success("API Key berhasil dihapus");
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  const mask = (key) => key.slice(0, 8) + "\u2022".repeat(20) + key.slice(-4);

  return (
    <div data-testid="api-keys-page" className="space-y-6 sm:space-y-8">
      <div className="rounded-3xl border border-border/70 bg-card/90 p-6 shadow-sm sm:p-8">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground">Access</p>
        <h1 className="mt-2 font-heading text-4xl font-black tracking-tight sm:text-5xl">API Keys</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground sm:text-base">Kelola banyak API key untuk banyak project, user, atau environment tanpa ubah login admin.</p>
      </div>

      <div className="flex justify-end">
        <button
          data-testid="create-api-key-button"
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 active:scale-[0.98]"
        >
          <Plus size={14} /> Buat Key
        </button>
      </div>

      {showCreate && (
        <div className="mb-6 rounded-3xl border border-border/70 bg-card/90 p-5 shadow-sm">
          <form onSubmit={handleCreate} className="flex flex-col gap-3 md:flex-row md:items-end">
            <div className="flex-1">
              <label className="mb-2 block text-xs font-bold uppercase tracking-widest text-muted-foreground">Nama Key</label>
              <input
                data-testid="api-key-name-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-2xl border border-input bg-background/80 px-4 py-3 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/20"
                placeholder="e.g., premku.com production"
                autoFocus
              />
            </div>
            <button
              data-testid="api-key-submit-button"
              type="submit"
              disabled={creating}
              className="rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50"
            >
              {creating ? "..." : "Generate"}
            </button>
            <button type="button" onClick={() => setShowCreate(false)} className="rounded-2xl border border-border bg-background/80 px-4 py-3 text-sm hover:bg-secondary transition-colors">
              Batal
            </button>
          </form>
        </div>
      )}

      <div className="space-y-3">
        {loading ? (
          <p className="text-muted-foreground text-sm">Memuat...</p>
        ) : keys.length === 0 ? (
          <div className="rounded-3xl border border-border/70 bg-card/90 p-8 text-center shadow-sm">
            <p className="text-muted-foreground text-sm">Belum ada API Key. Buat satu untuk memulai integrasi.</p>
          </div>
        ) : (
          visibleKeysList.map((k) => (
            <div key={k.id} data-testid={`api-key-${k.id}`} className="rounded-3xl border border-border/70 bg-card/90 p-4 shadow-sm">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-semibold">{k.name}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <code className="font-mono text-xs text-muted-foreground">
                      {visibleKeys[k.id] ? k.key : mask(k.key)}
                    </code>
                    <button onClick={() => setVisibleKeys((v) => ({ ...v, [k.id]: !v[k.id] }))} className="text-muted-foreground hover:text-foreground">
                      {visibleKeys[k.id] ? <EyeOff size={12} /> : <Eye size={12} />}
                    </button>
                    <button data-testid={`copy-key-${k.id}`} onClick={() => { navigator.clipboard.writeText(k.key); toast.success("Copied!"); }} className="text-muted-foreground hover:text-foreground">
                      <Copy size={12} />
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right text-xs text-muted-foreground">
                    <p>Created: {new Date(k.created_at).toLocaleDateString("id-ID")}</p>
                    {k.last_used && <p>Last: {new Date(k.last_used).toLocaleDateString("id-ID")}</p>}
                  </div>
                  <button data-testid={`delete-key-${k.id}`} onClick={() => handleDelete(k.id)} className="rounded-2xl p-2 text-red-500 hover:bg-red-50 transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {!loading && keys.length > 0 && (
        <div className="flex flex-col gap-3 rounded-3xl border border-border/70 bg-card/90 px-4 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-muted-foreground">
            Menampilkan {(currentPage - 1) * pageSize + 1}-{Math.min(currentPage * pageSize, keys.length)} dari {keys.length} API key
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="rounded-2xl border border-border bg-background/80 px-3 py-2 text-sm font-medium text-muted-foreground transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
            >
              Prev
            </button>
            <span className="rounded-2xl border border-border bg-background/80 px-3 py-2 text-sm font-medium">
              {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="rounded-2xl border border-border bg-background/80 px-3 py-2 text-sm font-medium text-muted-foreground transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
